import { useParams } from 'react-router-dom';
import { ROUTES } from '@/common/auth-routes';
import { buildAdminConsoleDashboardUrl } from '@/common/cross-app-urls';
import { getBaseDomain } from '@/common/tenant-utils';
import { validateOnboardingToken } from '@/features/onboarding/api/onboarding-api';
import { OnboardingBackground } from '@/features/onboarding/components/onboarding-background';
import { OnboardingTokenValidationStatusScreen } from '@/features/onboarding/components/onboarding-token-validation-status-screen';
import { OnboardingWizardContainer } from '@/features/onboarding/components/onboarding-wizard-container';
import { Onboarding } from '@/features/onboarding/components/primitives';
import { TenantOnboardingWizard } from '@/features/onboarding/components/wizards';
import {
  useTokenValidation,
  ValidationStatus,
} from '@/features/onboarding/hooks/use-token-validation';

function buildTenantDisplayUrl(tenantSlug: string): string {
  const baseDomain = getBaseDomain();

  return `app.${baseDomain}/${tenantSlug}`;
}

/**
 * Tenant Onboarding Page - Entry point for tenant-specific onboarding flow
 *
 * This page is at root level (/tenant-onboarding/:token) with no tenant in the path.
 * The tenant slug comes from the API validation response (validationResult.tenant_slug).
 *
 * Flow:
 * 1. Validate magic link token
 * 2. Show tenant onboarding wizard
 * 3. On completion, activate tenant and redirect to admin-console/dashboard
 */
export function TenantOnboardingPage() {
  const { token } = useParams<{ token: string }>();

  const { status, validationResult, error } = useTokenValidation({
    token,
    validateToken: validateOnboardingToken,
  });

  const tenantSlug = validationResult?.tenant_slug ?? null;

  const handleComplete = () => {
    if (tenantSlug) {
      window.location.href = buildAdminConsoleDashboardUrl(tenantSlug);
    } else {
      console.warn('[TenantOnboarding] Missing tenant slug on completion');
      window.location.href = ROUTES.TENANT_SELECTION;
    }
  };

  if (status !== ValidationStatus.VALID) {
    return (
      <OnboardingTokenValidationStatusScreen
        status={status}
        validationResult={validationResult}
        error={error}
        buildSignInUrl={buildAdminConsoleDashboardUrl}
      />
    );
  }

  if (!token || !validationResult) {
    return null;
  }

  return (
    <OnboardingWizardContainer>
      <OnboardingBackground />
      <Onboarding.Dialog open={true} dismissible={false} size='md'>
        <TenantOnboardingWizard
          token={token}
          userName={
            validationResult.user_first_name ??
            validationResult.user_display_name
          }
          companyName={validationResult.tenant_display_name}
          tenantUrl={tenantSlug ? buildTenantDisplayUrl(tenantSlug) : undefined}
          onComplete={handleComplete}
        />
      </Onboarding.Dialog>
    </OnboardingWizardContainer>
  );
}

export default TenantOnboardingPage;
